import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Search,
  Send,
  FileQuestion,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Eye,
  RefreshCw,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TranslatorMissingProject = () => {
  const [activeTab, setActiveTab] = useState("search");
  
  // Search state
  const [searchForm, setSearchForm] = useState({
    main_title: "",
    episode_title: "",
    atlas_job_id: "",
    po_number: "",
  });
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Request state
  const [selectedProject, setSelectedProject] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestForm, setRequestForm] = useState({
    reason: "",
    note: "",
    confirmation: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // My requests state
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchMyRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const res = await axios.get(`${API}/claims/my-requests`);
      setMyRequests(res.data.requests || []);
    } catch (error) {
      toast.error("Failed to load your requests");
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchForm.main_title || searchForm.main_title.trim().length < 2) {
      toast.error("Please enter at least 2 characters for Main Title");
      return;
    }

    setSearching(true);
    setHasSearched(true);
    try {
      const res = await axios.post(`${API}/claims/search`, {
        main_title: searchForm.main_title.trim(),
        episode_title: searchForm.episode_title.trim() || null,
        atlas_job_id: searchForm.atlas_job_id.trim() || null,
        po_number: searchForm.po_number.trim() || null,
      });
      setSearchResults(res.data.results || []);
      if (res.data.limited) {
        toast.info("Results limited to 50. Try more specific search terms.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const openRequestDialog = (project) => {
    setSelectedProject(project);
    setRequestForm({ reason: "", note: "", confirmation: false });
    setShowRequestDialog(true);
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.confirmation) {
      toast.error("Please confirm this project belongs to your completed work");
      return;
    }
    if (!requestForm.reason || requestForm.reason.trim().length < 10) {
      toast.error("Please provide a detailed reason (at least 10 characters)");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/claims/request`, {
        task_id: selectedProject.id,
        reason: requestForm.reason.trim(),
        note: requestForm.note.trim() || null,
        confirmation: requestForm.confirmation,
      });
      toast.success("Claim request submitted successfully!");
      setShowRequestDialog(false);
      setSelectedProject(null);
      fetchMyRequests();
      // Switch to My Requests tab
      setActiveTab("requests");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: "bg-amber-100 text-amber-700", icon: Clock, label: "Pending" },
      approved: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Approved" },
      rejected: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Rejected" },
      under_review: { color: "bg-blue-100 text-blue-700", icon: Eye, label: "Under Review" },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border-0 text-[10px] font-medium`}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const clearSearch = () => {
    setSearchForm({ main_title: "", episode_title: "", atlas_job_id: "", po_number: "" });
    setSearchResults([]);
    setHasSearched(false);
  };

  return (
    <div className="w-full px-4 sm:px-6 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <FileQuestion className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope, system-ui" }}>
              Missing Project Request
            </h1>
            <p className="text-xs text-slate-500">Search and claim projects missing from your invoice</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="search" className="text-xs" data-testid="tab-search">
            <Search className="w-3.5 h-3.5 mr-1.5" />
            Search Projects
          </TabsTrigger>
          <TabsTrigger value="requests" className="text-xs" data-testid="tab-requests">
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            My Requests
            {myRequests.filter(r => r.status === "pending").length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {myRequests.filter(r => r.status === "pending").length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Search Tab */}
        <TabsContent value="search" className="space-y-4">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-xl">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-blue-800 font-medium">How to use this feature</p>
              <p className="text-[11px] text-blue-600 mt-0.5">
                Search for a project you worked on but is missing from your invoice. 
                If found, you can request reassignment. All requests are reviewed by admin.
              </p>
            </div>
          </div>

          {/* Search Form */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                Search Projects
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">
                      Main Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={searchForm.main_title}
                      onChange={(e) => setSearchForm({ ...searchForm, main_title: e.target.value })}
                      placeholder="e.g., The Middle S9"
                      className="h-9 text-sm"
                      data-testid="search-main-title"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">Episode Title</Label>
                    <Input
                      value={searchForm.episode_title}
                      onChange={(e) => setSearchForm({ ...searchForm, episode_title: e.target.value })}
                      placeholder="Optional"
                      className="h-9 text-sm"
                      data-testid="search-episode-title"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">Atlas Job ID</Label>
                    <Input
                      value={searchForm.atlas_job_id}
                      onChange={(e) => setSearchForm({ ...searchForm, atlas_job_id: e.target.value })}
                      placeholder="Optional"
                      className="h-9 text-sm"
                      data-testid="search-job-id"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-600">PO Number</Label>
                    <Input
                      value={searchForm.po_number}
                      onChange={(e) => setSearchForm({ ...searchForm, po_number: e.target.value })}
                      placeholder="Optional"
                      className="h-9 text-sm"
                      data-testid="search-po-number"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="submit" disabled={searching} className="bg-teal-600 hover:bg-teal-700 h-9 text-xs" data-testid="search-btn">
                    {searching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
                    {searching ? "Searching..." : "Search"}
                  </Button>
                  {hasSearched && (
                    <Button type="button" variant="outline" onClick={clearSearch} className="h-9 text-xs">
                      Clear
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Search Results */}
          {hasSearched && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-sm font-semibold text-slate-800">
                  Search Results ({searchResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {searchResults.length > 0 ? (
                  <div className="overflow-auto max-h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-[10px] font-semibold uppercase w-[200px]">Title / Episode</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase text-center w-16">Type</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase w-20">Language</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase text-center w-16">Dur.</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase w-[140px]">Assigned To</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase w-20">PO</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase w-24">Due Date</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase text-center w-20">Status</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase text-center w-24">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {searchResults.map((result) => (
                          <TableRow key={result.id} className="hover:bg-slate-50/50">
                            <TableCell className="py-2">
                              <p className="text-xs font-medium text-slate-700 line-clamp-1">{result.main_title}</p>
                              <p className="text-[10px] text-slate-400 line-clamp-1">
                                {result.episode_title} {result.episode_number ? `Ep ${result.episode_number}` : ""}
                              </p>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-semibold">
                                {result.task_type || "-"}
                              </span>
                            </TableCell>
                            <TableCell className="text-[11px] text-slate-600 truncate max-w-[80px]">
                              {result.language || "-"}
                            </TableCell>
                            <TableCell className="text-center text-[11px] font-mono text-slate-600">
                              {result.duration_words?.toFixed(2) || "-"}
                            </TableCell>
                            <TableCell className="text-[11px] text-slate-600 truncate max-w-[140px]">
                              {result.resource || "-"}
                            </TableCell>
                            <TableCell className="text-[11px] font-mono text-slate-600">
                              {result.po_number || "-"}
                            </TableCell>
                            <TableCell className="text-[11px] text-slate-600">
                              {result.due_date || "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={`text-[9px] border-0 ${
                                result.task_status === "Completed" 
                                  ? "bg-emerald-100 text-emerald-700" 
                                  : "bg-slate-100 text-slate-600"
                              }`}>
                                {result.task_status || "Unknown"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                size="sm"
                                onClick={() => openRequestDialog(result)}
                                className="h-7 text-[10px] bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid={`request-btn-${result.id}`}
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Request
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="py-12 text-center">
                    <Search className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-slate-500">No projects found</p>
                    <p className="text-xs text-slate-400 mt-1">Try different search terms</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* My Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-800">Your Claim Requests</h2>
            <Button variant="outline" size="sm" onClick={fetchMyRequests} disabled={loadingRequests} className="h-8 text-xs">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingRequests ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {loadingRequests ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin" />
            </div>
          ) : myRequests.length > 0 ? (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-auto max-h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] font-semibold uppercase w-24">Request ID</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase">Project</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase w-28">Requested On</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase text-center w-24">Status</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase w-28">Decision Date</TableHead>
                        <TableHead className="text-[10px] font-semibold uppercase text-center w-16">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myRequests.map((req) => (
                        <TableRow key={req.id} className="hover:bg-slate-50/50">
                          <TableCell className="font-mono text-[11px] text-slate-600">{req.id}</TableCell>
                          <TableCell>
                            <p className="text-xs font-medium text-slate-700 line-clamp-1">{req.main_title}</p>
                            <p className="text-[10px] text-slate-400 line-clamp-1">{req.episode_title}</p>
                          </TableCell>
                          <TableCell className="text-[11px] text-slate-600">
                            {req.created_at ? new Date(req.created_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="text-center">{getStatusBadge(req.status)}</TableCell>
                          <TableCell className="text-[11px] text-slate-600">
                            {req.decision_date ? new Date(req.decision_date).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedRequest(req)}
                              className="h-7 w-7 p-0"
                              data-testid={`view-request-${req.id}`}
                            >
                              <Eye className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="py-12 text-center">
                <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-500">No requests yet</p>
                <p className="text-xs text-slate-400 mt-1">Search and request projects from the Search tab</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send className="w-4 h-4 text-amber-500" />
              Request Project Reassignment
            </DialogTitle>
            <DialogDescription>
              Submit a claim request for this project
            </DialogDescription>
          </DialogHeader>

          {selectedProject && (
            <div className="space-y-4">
              {/* Project Info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-800">{selectedProject.main_title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedProject.episode_title}</p>
                <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-400">
                  <span>Type: {selectedProject.task_type}</span>
                  <span>•</span>
                  <span>PO: {selectedProject.po_number || "-"}</span>
                  <span>•</span>
                  <span>Job ID: {selectedProject.atlas_job_id || "-"}</span>
                </div>
                <div className="mt-2 text-[10px] text-slate-500">
                  Currently assigned to: <span className="font-medium">{selectedProject.resource || "Unknown"}</span>
                </div>
              </div>

              {/* Warning for invoiced/locked projects */}
              {(selectedProject.invoice_status === "invoiced" || selectedProject.batch_id) && (
                <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-700">
                    This project may already be invoiced or locked in a batch. Admin approval will be required.
                  </p>
                </div>
              )}

              {/* Reason */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">
                  Reason for Request <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={requestForm.reason}
                  onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                  placeholder="Please explain why this project should be assigned to you..."
                  rows={3}
                  className="text-sm"
                  data-testid="request-reason"
                />
              </div>

              {/* Note */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Additional Notes (Optional)</Label>
                <Textarea
                  value={requestForm.note}
                  onChange={(e) => setRequestForm({ ...requestForm, note: e.target.value })}
                  placeholder="Any additional information..."
                  rows={2}
                  className="text-sm"
                  data-testid="request-note"
                />
              </div>

              {/* Confirmation */}
              <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                <Checkbox
                  id="confirmation"
                  checked={requestForm.confirmation}
                  onCheckedChange={(v) => setRequestForm({ ...requestForm, confirmation: v })}
                  data-testid="request-confirmation"
                />
                <Label htmlFor="confirmation" className="text-xs text-slate-600 cursor-pointer leading-relaxed">
                  I confirm this project belongs to my completed work and I have not been credited for it.
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmitRequest}
              disabled={submitting || !requestForm.confirmation}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-request-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileQuestion className="w-4 h-4 text-teal-500" />
              Request Details
            </DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-500">{selectedRequest.id}</span>
                {getStatusBadge(selectedRequest.status)}
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm font-semibold text-slate-800">{selectedRequest.main_title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedRequest.episode_title}</p>
                <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-400">
                  <span>Type: {selectedRequest.task_type}</span>
                  <span>•</span>
                  <span>PO: {selectedRequest.po_number || "-"}</span>
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-[10px] font-medium text-slate-500 uppercase">Your Reason</p>
                  <p className="text-xs text-slate-700 mt-0.5">{selectedRequest.reason}</p>
                </div>
                {selectedRequest.note && (
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 uppercase">Your Note</p>
                    <p className="text-xs text-slate-700 mt-0.5">{selectedRequest.note}</p>
                  </div>
                )}
              </div>

              {selectedRequest.admin_note && (
                <div className={`p-3 rounded-lg ${
                  selectedRequest.status === "approved" 
                    ? "bg-emerald-50 border border-emerald-200" 
                    : selectedRequest.status === "rejected"
                    ? "bg-red-50 border border-red-200"
                    : "bg-blue-50 border border-blue-200"
                }`}>
                  <p className="text-[10px] font-medium text-slate-500 uppercase">Admin Response</p>
                  <p className="text-xs mt-0.5">{selectedRequest.admin_note}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-500">
                <div>
                  <p className="font-medium">Requested On</p>
                  <p>{selectedRequest.created_at ? new Date(selectedRequest.created_at).toLocaleString() : "-"}</p>
                </div>
                {selectedRequest.decision_date && (
                  <div>
                    <p className="font-medium">Decision Date</p>
                    <p>{new Date(selectedRequest.decision_date).toLocaleString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSelectedRequest(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranslatorMissingProject;
